import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Assortment, AssortmentService, AuthService } from 'gung-standard';
import { Subject, filter, first, forkJoin, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { fnProductName } from '../../pipes/product-name.pipe';
import { OttoOlsenPimEditConfigService } from '../../services/otto-olsen-pim-edit-config.service';

export interface OttoOlsenCategoryGroup {
  assortment: Assortment;
  children: Assortment[];
}

/**
 * "Alle kategorier": every sub category in the user's assortment, grouped under a band per main category.
 * The cards mirror OttoOlsenAssortmentTreeListViewComponent, but link with absolute /articles/<main>/<sub> paths.
 */
@Component({
  selector: 'otto-olsen-all-categories',
  standalone: false,
  templateUrl: './otto-olsen-all-categories.component.html',
  styleUrl: './otto-olsen-all-categories.component.scss'
})
export class OttoOlsenAllCategoriesComponent implements OnInit, OnDestroy {
  groups: OttoOlsenCategoryGroup[] = [];
  loading = true;

  assortmentPimUrl: string = environment.externalPimUrl + 'assortment/';
  isAssortmentManager = false;
  pimEditUrlHide = false;

  private authService = inject(AuthService);
  private assortmentService = inject(AssortmentService);
  private translateService = inject(TranslateService);
  private ottoOlsenPimEditConfigService = inject(OttoOlsenPimEditConfigService);
  private unsubscribe = new Subject<void>();

  ngOnInit(): void {
    this.ottoOlsenPimEditConfigService.pimEditUrlHide
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(pimEditUrlHide => (this.pimEditUrlHide = pimEditUrlHide));

    this.authService
      .getCurrentUser()
      .pipe(
        filter(user => !!user),
        first(),
        switchMap(user =>
          forkJoin([
            this.assortmentService.getAssortmentRecursiveNoProducts(user.assortment).pipe(first()),
            this.authService.getRoles().pipe(first())
          ])
        )
      )
      .subscribe(([assortment, userRoles]) => {
        this.isAssortmentManager = this.ottoOlsenPimEditConfigService.hasPimEditUrlsRoles(userRoles, 'assortments');
        // Same visibility rule as the category pages: SALES/ADMIN see everything, others only what is active in B2B
        const seeAllAssortments = userRoles.includes('SALES') || userRoles.includes('ADMIN');
        this.groups = (assortment?.children || [])
          .map(main => ({
            assortment: main,
            children: (main.children || []).filter(child => seeAllAssortments || this.isActiveInB2B(child))
          }))
          .filter(group => group.children.length > 0);
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  isActiveInB2B(assortment: Assortment): boolean {
    return assortment?.extra?.pim?.activeInB2B + '' === 'true';
  }

  /** Main category heading: the translated assortment name used on the start page, otherwise the PIM display name */
  getMainCategoryName(assortment: Assortment): string {
    const currentLang = this.translateService.currentLang || 'no';
    return assortment?.extra?.i18?.[currentLang]?.assortmentName || fnProductName(assortment);
  }
}
